import Download16 from '@carbon/icons-react/es/download/16';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
} from 'ag-grid-community';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { BillingAPI } from 'api/billing';
import { CurrencyCode, InvoiceStatus } from 'api/billing/types';
import { Button } from 'components/atoms/button';
import { Grid } from 'components/atoms/grid';
import { TableSkeleton } from 'components/atoms/table';
import { DownloadFromApi } from 'components/molecules/download-link/download-link';
import { LoadingOverlay } from 'components/organisms/tasks/components';
import {
  TranslationKey,
  useTranslate,
  useTranslation,
} from 'hooks/use-translation';
import { useInvoiceListQuery } from 'queries/billing';
import { formatAmount } from './utils';

const DownloadButton = styled(Button)`
  visibility: hidden;
`;

const GridWrapper = styled.div`
  position: relative;
  margin-top: var(--spacing-08);
`;

const GridTitle = styled.b`
  margin-left: var(--spacing-04);
`;

const StyledGrid = styled(Grid)`
  margin-top: var(--spacing-04);

  .ag-row-hover {
    ${DownloadButton} {
      visibility: visible;
    }
  }
`;

const StyledLoadingOverlay = styled(LoadingOverlay)`
  padding-left: 0;
`;

const DateRenderer: FC<ICellRendererParams> = ({ data }) => (
  <>{moment(data.date).format('MMM D, YYYY')}</>
);

const DownloadRenderer: FC<
  ICellRendererParams & {
    subscriptionId: number;
  }
> = ({ data, subscriptionId }) => {
  const [downloading, setDownloading] = useState(false);

  const _api = useCallback(async () => {
    setDownloading(true);
    const { downloadUrl } = await BillingAPI.downloadInvoice(
      subscriptionId,
      data.id,
    );

    requestAnimationFrame(() => setDownloading(false));

    return { url: downloadUrl };
  }, [data.id, subscriptionId]);

  return (
    <DownloadFromApi api={_api}>
      <DownloadButton type="link" icon={<Download16 />} loading={downloading} />
    </DownloadFromApi>
  );
};

const GridLoadingOverlay = () => {
  return (
    <StyledLoadingOverlay>
      <TableSkeleton rowCount={10} columnCount={5} />
    </StyledLoadingOverlay>
  );
};

export const InvoiceList = ({
  subscriptionId,
  currencyCode,
}: {
  subscriptionId: number;
  currencyCode: CurrencyCode;
}) => {
  const translate = useTranslate();
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const { data: invoiceListResponse, isLoading } =
    useInvoiceListQuery(subscriptionId);

  const invoiceTotalText = useTranslation('billing.invoice.total', {
    currencyCode,
  });
  const licensesText = useTranslation('billing.licenses');

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  useEffect(() => {
    if (isLoading || !invoiceListResponse) {
      return;
    }
    const { invoices } = invoiceListResponse;

    gridApi?.setRowData(
      invoices.map((_invoice) => {
        const { invoiceId, invoicedDate, description, seats, status } =
          _invoice;
        const statusTranslationKey =
          `billing.invoice.status.${InvoiceStatus[status]}`.toLowerCase() as TranslationKey;

        return {
          id: invoiceId,
          date: invoicedDate,
          details: description,
          seats,
          total: formatAmount(_invoice.invoiceTotal),
          status: translate(statusTranslationKey),
        };
      }),
    );
  }, [gridApi, invoiceListResponse, isLoading, translate]);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { field: 'date', width: 120, cellRenderer: 'dateRenderer' },
      { field: 'details', flex: 1 },
      {
        field: 'seats',
        width: 120,
        type: 'rightAligned',
        headerName: licensesText,
        cellStyle: { 'justify-content': 'flex-end' },
      },
      {
        field: 'total',
        width: 200,
        headerName: invoiceTotalText,
        type: 'rightAligned',
        cellStyle: { 'justify-content': 'flex-end' },
      },
      { field: 'status', width: 120 },
      {
        field: 'download',
        width: 50,
        headerName: '',
        cellRenderer: 'downloadRenderer',
        cellRendererParams: {
          subscriptionId,
        },
      },
    ],
    [invoiceTotalText, licensesText, subscriptionId],
  );

  const defaultColumnDef = useMemo(
    () => ({
      suppressMenu: true,
    }),
    [],
  );

  return (
    <GridWrapper>
      <GridTitle>
        <FormattedMessage id="invoices" />
      </GridTitle>
      <StyledGrid
        className="ag-theme-alpine"
        animateRows
        suppressContextMenu
        suppressCellFocus
        suppressHorizontalScroll
        onGridReady={onGridReady}
        domLayout="autoHeight"
        loadingOverlayComponent="GridLoadingOverlay"
        columnDefs={columnDefs}
        defaultColDef={defaultColumnDef}
        components={{
          dateRenderer: DateRenderer,
          downloadRenderer: DownloadRenderer,
          GridLoadingOverlay,
        }}
      />
    </GridWrapper>
  );
};
